import { IGetWorkspacesQuery } from 'pages/CommandCenter/Accounts/servicesConfig';
import { EYesNo, SortOrders } from 'config/general';
import { IPaging } from 'types/IPaging';
import { ICustomMap } from 'types/ICustomMap';

export enum AccountStatus {
    new = 'new',
    active = 'active',
    deactivated = 'deactivated',
}

export interface IWorkspace {
    customer_type: string;
    id_str: string;
    name: string;
    workspace_logo_path: string;
    workspace_name: string;
    workspace_photo_path: string;
    workspace_primary_color: string;
    cap_settings?: IWorkspaceSettings;
    activated_at?: string;
    deactivated_at?: string;
    status?: AccountStatus;
    payment_status?: string;
    project_details?: {
        sf_link?: string;
        project_tracker_link?: string;
        qb_account_link?: string;
        qb_account_id?: string;
    };
    last_template_update: string | null;
}

export interface IFormValues {
    name: string;
    workspaceName: string;
    customerType: string;
    color: string;
    enableFaceRecognition?: EYesNo;
    rentgpt?: boolean;
    sf_link?: string;
    project_tracker_link?: string;
    qb_account_link?: string;
    qb_account_id?: string;
}

export interface IAdminFormValues {
    firstName: string;
    lastName: string;
    email: string;
    sendInvite: boolean;
}

export interface IWorkspaceSettings {
    /** Hours "Keep unlocked" may hold a door open in this workspace (cap_settings; 1–24, default 8). */
    max_keep_unlocked_hours?: number;
    // Door schedule overrides on/off for this workspace (replaced the DOOR_RELAY_OVERRIDE_COMPANY_IDS env var).
    enable_door_overrides?: boolean;
    enable_face_recognition: boolean;
    enable_auth_providers: boolean;
    enable_end_user_billing: boolean;
    no_of_key_card_readers: number | null;
    no_of_key_cards: number | null;
    no_of_users: number | null;
    no_of_access_points: number | null;
    no_of_front_desks: number | null;
    no_of_tenants: number | null;
    enable_windows_agents?: boolean | null;
    enable_eagle_eye_integration?: boolean | null;
    enable_milestone?: boolean | null;
    enable_entrata_integration?: boolean | null;
    enable_real_page_integration?: boolean | null;
    enable_yardi_integration?: boolean | null;
    enable_health_check?: boolean | null;
    enable_smart_lock_integration?: boolean | null;
    enable_brivo_integration?: boolean | null;
    enable_rent_gpt: boolean;
    max_smart_locks_allowed?: number;
    enable_acu: boolean;
    max_qr_codes_allowed?: number;
    enable_package_scanning?: boolean;
    location_check?: boolean;
    allow_only_one_device_per_user?: boolean;
    enable_call_box_integration?: boolean;
    no_of_virtual_phone_numbers?: number;
    readonly remaining_locks: number | null;
    readonly added_locks: number | null;
    igloo_api_key?: string;
    enable_buildium_integration?: boolean | null;
    enable_wallet: boolean;
    /** Master switch for prepaid bulk wallet passes (cap_settings). */
    prepaid_wallet_enabled?: boolean;
    bulk_wallet_total_passes?: number | null;
    bulk_wallet_allocated_passes?: number | null;
    bulk_wallet_remaining_passes?: number | null;
    bulk_wallet_has_active_plan?: boolean;
    bulk_wallet_plan_id?: number | null;
    bulk_wallet_plan_status?: string | null;
    bulk_wallet_subscription_start_dt?: string | null;
    bulk_wallet_subscription_end_dt?: string | null;
    /** When true, admins can play motion alert videos in Activity Feed (cap_settings). */
    is_motion_alerts_paid?: boolean;
    /** Company-level ZKTeco entitlement (cap_settings); surfaced as zkteco_enabled on the profile API. */
    enable_zkteco?: boolean;
    /** Account-level on/off for Package Room (cap_settings). Gate layer 2 of 3 on the backend. */
    enable_package_concierge?: boolean;
    /** Master switch for the whole Amenity Booking console (cap_settings); gates /amenities/ entirely. */
    enable_amenity_booking?: boolean;
    /** SMS notification channel for amenity bookings (cap_settings); independent of the master switch. */
    enable_amenity_sms?: boolean;
    /** Whether ANY amenity booking on this account may be charged (cap_settings) — money gate, independent of a given amenity's own price. */
    enable_amenity_payments?: boolean;
    /** Whether joining an amenity waitlist can be charged (cap_settings), independent of enable_amenity_payments. */
    enable_amenity_waitlist_charge?: boolean;
    /** Basis-points platform fee on paid amenity bookings (cap_settings); backend clamps 0-10000. */
    amenity_platform_fee_bps?: number;
    /** Basis-points clerkage fee retained on a charged waitlist join (cap_settings); backend clamps 0-10000. */
    amenity_waitlist_clerkage_bps?: number;
}

export interface ReqWorkspacesQuery extends IPaging {
    unified_name?: string;
    site_address?: string;
    company_id?: string;
    sf_link?: string;
    project_tracker_link?: string;
    qb_account_id?: string;
    account_status?: string;
    payment_status?: string;
    customer_type?: string;
    sort_by?: string;
    accounts_with_no_template?: boolean;
    sort_order?: SortOrders;
}

export type ReqUpdateAccount = {
    id: string;
    workspace_name: string;
    name: string;
    workspace_primary_color: string;
    customer_type: string;
    project_details: {
        sf_link?: string;
        project_tracker_link?: string;
        qb_account_link?: string;
        qb_account_id?: string;
    };
};

export enum ECustomerType {
    demo = 'demo',
    directCustomer = 'direct-customer',
    integrator = 'integrator',
}

export type AccountSummary = {
    total_face_unlocks_successful: number;
    total_intercom_calls: number;
    total_number_of_events: number;
    total_pin_unlocks_successful: number;
    total_unlock_failures: number;
    unique_users_accessed: number;
};

export type TMotionAlertsSettings = {
    is_enabled: boolean;
    max_daily_events: number;
    video_duration: number;
    video_resolution: string;
    video_fps: number;
    retention_days: number;
};

export type ReqSaveMotionAlertsSettings = {
    is_enabled: boolean;
    max_daily_events: number;
    video_duration: number;
    video_resolution: string;
    video_fps: number;
    retention_days: number;
};

/** Nested `schedule` on GET /motion-alerts/schedule-mapping. */
export type TMotionAlertMappedSchedule = {
    schedule_id_str: string;
    schedule_name: string;
    access_type: string;
    timezone: string;
    standard_schedule: ICustomMap | null;
};

/** Root payload on GET /motion-alerts/schedule-mapping. */
export type TMotionAlertsScheduleMapping = {
    schedule_id_str: string | null;
    schedule: TMotionAlertMappedSchedule | null;
};

export type ReqPostMotionAlertsScheduleMapping = {
    /** Same as `ISchedule.id_str` from `/user_schedules/`. */
    schedule_id: string;
};

export type TFilters = IGetWorkspacesQuery;
export type THandleDeleteWorkspace = (id: string) => void;
export type THandleFormSubmit = (values: IFormValues) => void;
export type THandleAdminFormSubmit = (values: IAdminFormValues) => void;
